import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Transient feedback after a write.
 *
 * A toast is for confirming something happened, not for reporting a failure the user must act
 * on — that belongs inline, next to the control that failed. So the API is deliberately
 * narrow: a title, an optional detail, and a tone.
 */

export type ToastTone = 'default' | 'success' | 'warning' | 'danger';

export interface Toast {
  id: string;
  title: string;
  body?: string;
  tone: ToastTone;
}

interface ToastContextValue {
  show: (title: string, options?: { body?: string; tone?: ToastTone; durationMs?: number }) => void;
  success: (title: string, body?: string) => void;
  error: (title: string, body?: string) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used inside <ToastProvider>.');
  }

  return context;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback<ToastContextValue['show']>(
    (title, options = {}) => {
      const id = crypto.randomUUID();
      const tone = options.tone ?? 'default';

      setToasts((current) => {
        // Cap the stack: more than three at once and they stop being readable.
        const next = [...current, { id, title, body: options.body, tone }];
        return next.slice(-3);
      });

      // An error stays longer, because it usually carries a detail worth reading.
      const duration = options.durationMs ?? (tone === 'danger' ? 8000 : 4000);

      window.setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  const success = useCallback(
    (title: string, body?: string) => show(title, { body, tone: 'success' }),
    [show],
  );

  const error = useCallback(
    (title: string, body?: string) => show(title, { body, tone: 'danger' }),
    [show],
  );

  const value = useMemo(() => ({ show, success, error, dismiss }), [show, success, error, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}

      <div className="cma-toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`cma-toast${toast.tone !== 'default' ? ` cma-toast--${toast.tone}` : ''}`}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="cma-toast__title">{toast.title}</div>
              {toast.body && <div className="cma-toast__body">{toast.body}</div>}
            </div>

            <button
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss"
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                padding: 0,
                fontSize: 14,
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
